import { FC } from "react";
import { Box, Container, Heading, Text, VStack, Grid, Flex, List, ListItem } from "@chakra-ui/react";
import { LandingPageContent } from "@/constants/landingPages";

interface LandingPricingSectionProps {
  content: LandingPageContent["pricing"];
}

export const LandingPricingSection: FC<LandingPricingSectionProps> = ({ content }) => {
  // Combine all features into one list
  const allFeatures = [
    ...content.mainPricing.setup.features,
    ...content.mainPricing.monthly.features,
  ];

  return (
    <Box py={{ base: "16", md: "24" }} bg="gray.50">
      <Container maxW="7xl">
        <VStack spacing="12">
          {/* Section header */}
          <VStack spacing="4" textAlign="center" maxW="800px">
            <Heading as="h2" fontSize={{ base: "3xl", md: "4xl" }} color="gray.900">
              {content.title}
            </Heading>
            <Text fontSize={{ base: "lg", md: "xl" }} color="gray.600">
              {content.subtitle}
            </Text>
          </VStack>

          {/* Main Pricing: Professional Unified Card */}
          <Box w="100%" maxW="900px" mx="auto">
            <Box
              bg="white"
              borderRadius="xl"
              boxShadow="lg"
              border="1px solid"
              borderColor="gray.200"
              overflow="hidden"
            >
              {/* Price breakdown title */}
              <Box
                bg="gray.900"
                color="white"
                py="4"
                px="6"
                textAlign="center"
              >
                <Text fontSize="lg" fontWeight="600">
                  {content.headerTitle}
                </Text>
              </Box>

              {/* Two-column pricing */}
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                gap="0"
              >
                {/* Setup Fee - Left */}
                <Box
                  p="8"
                  borderRight={{ base: "none", md: "1px solid" }}
                  borderBottom={{ base: "1px solid", md: "none" }}
                  borderColor="gray.200"
                >
                  <VStack spacing="6" align="stretch">
                    {/* Icon & Title */}
                    <VStack spacing="3">
                      <Box
                        fontSize="3xl"
                        w="16"
                        h="16"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="lg"
                        bg="gray.100"
                      >
                        {content.mainPricing.setup.icon}
                      </Box>
                      <Heading as="h3" fontSize="lg" color="gray.900" textAlign="center">
                        {content.mainPricing.setup.title}
                      </Heading>
                      <Text color="gray.600" fontSize="sm" textAlign="center">
                        {content.mainPricing.setup.description}
                      </Text>
                    </VStack>

                    {/* Price */}
                    <Box textAlign="center" py="4">
                      <Flex align="baseline" justify="center" gap="1" mb="1">
                        <Text fontSize="sm" color="gray.500" fontWeight="500">{content.pricePrefix}</Text>
                        <Text fontSize="4xl" fontWeight="700" color="gray.900" lineHeight="1">
                          {content.mainPricing.setup.priceFrom}
                        </Text>
                      </Flex>
                      <Text color="gray.500" fontSize="sm">
                        {content.mainPricing.setup.priceLabel}
                      </Text>
                    </Box>
                  </VStack>
                </Box>

                {/* Monthly Subscription - Right */}
                <Box p="8">
                  <VStack spacing="6" align="stretch">
                    {/* Icon & Title */}
                    <VStack spacing="3">
                      <Box
                        fontSize="3xl"
                        w="16"
                        h="16"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="lg"
                        bg="gray.100"
                      >
                        {content.mainPricing.monthly.icon}
                      </Box>
                      <Heading as="h3" fontSize="lg" color="gray.900" textAlign="center">
                        {content.mainPricing.monthly.title}
                      </Heading>
                      <Text color="gray.600" fontSize="sm" textAlign="center">
                        {content.mainPricing.monthly.description}
                      </Text>
                    </VStack>

                    {/* Price */}
                    <Box textAlign="center" py="4">
                      <Flex align="baseline" justify="center" gap="1" mb="1">
                        <Text fontSize="sm" color="gray.500" fontWeight="500">{content.pricePrefix}</Text>
                        <Text fontSize="4xl" fontWeight="700" color="gray.900" lineHeight="1">
                          {content.mainPricing.monthly.priceFrom}
                        </Text>
                      </Flex>
                      <Text color="gray.500" fontSize="sm">
                        {content.mainPricing.monthly.priceLabel}
                      </Text>
                    </Box>
                  </VStack>
                </Box>
              </Grid>

              {/* Combined Features List */}
              <Box
                px="8"
                py="6"
                borderTop="1px solid"
                borderColor="gray.200"
              >
                <Heading as="h4" fontSize="md" color="gray.900" mb="4" textAlign="center">
                  {content.featuresTitle}
                </Heading>
                <Grid
                  templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                  gap="3"
                >
                  {allFeatures.map((feature, idx) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Flex key={`feature-${idx}`} alignItems="flex-start" gap="2">
                      <Box
                        w="5"
                        h="5"
                        borderRadius="full"
                        bg="gray.900"
                        color="white"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontSize="xs"
                        fontWeight="bold"
                        flexShrink={0}
                      >
                        ✓
                      </Box>
                      <Text color="gray.700" fontSize="sm" lineHeight="1.6">{feature}</Text>
                    </Flex>
                  ))}
                </Grid>
              </Box>

              {/* Bottom summary */}
              <Box
                bg="gray.50"
                py="4"
                px="6"
                textAlign="center"
                borderTop="1px solid"
                borderColor="gray.200"
              >
                <Text fontSize="sm" color="gray.600" fontWeight="500">
                  {content.summaryText}
                </Text>
              </Box>
            </Box>
          </Box>

          {/* Custom Development - Optional Section */}
          <Box w="100%" maxW="900px" mx="auto">
            <Box
              bg="white"
              borderRadius="xl"
              p={{ base: "6", md: "8" }}
              boxShadow="md"
              border="1px solid"
              borderColor="gray.200"
              position="relative"
            >
              {/* Optional Badge */}
              <Box
                position="absolute"
                top="-3"
                left="50%"
                transform="translateX(-50%)"
                bg="gray.900"
                color="white"
                px="4"
                py="1"
                borderRadius="full"
                fontSize="xs"
                fontWeight="600"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                {content.customDev.badge}
              </Box>

              <Flex
                direction={{ base: "column", md: "row" }}
                gap="8"
                align="center"
                pt="4"
              >
                {/* Left: Icon & Title */}
                <Flex
                  direction="column"
                  align="center"
                  minW={{ base: "auto", md: "250px" }}
                  textAlign="center"
                >
                  <Box
                    fontSize="3xl"
                    w="16"
                    h="16"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="lg"
                    bg="gray.100"
                    mb="4"
                  >
                    {content.customDev.icon}
                  </Box>
                  <Heading as="h3" fontSize="lg" color="gray.900" mb="2">
                    {content.customDev.title}
                  </Heading>
                </Flex>

                {/* Right: Description & Examples */}
                <VStack align="flex-start" spacing="4" flex="1">
                  <Text color="gray.700" lineHeight="1.7">
                    {content.customDev.description}
                  </Text>
                  <List spacing="2.5">
                    {content.customDev.examples.map((example, idx) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <ListItem key={`custom-${idx}`} display="flex" alignItems="flex-start" gap="2">
                        <Box
                          w="5"
                          h="5"
                          borderRadius="full"
                          bg="gray.900"
                          color="white"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontSize="xs"
                          fontWeight="bold"
                          flexShrink={0}
                          mt="0.5"
                        >
                          ✓
                        </Box>
                        <Text color="gray.600" fontSize="sm" lineHeight="1.6">{example}</Text>
                      </ListItem>
                    ))}
                  </List>
                </VStack>
              </Flex>
            </Box>
          </Box>

          {/* Bottom note */}
          <Text fontSize="sm" color="gray.500" textAlign="center" fontWeight="500">
            {content.note}
          </Text>
        </VStack>
      </Container>
    </Box>
  );
};
